import { AlmanacFile, SetState } from 'types'
import { NewInlineOption, HandleEnterProps, HandleKeydownProps } from './types'
import { isAlmanacDocUrl } from 'utils/urlUtils'
import { OnOpenImportModalProps } from './types'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { FolderClient } from 'api/clients/FolderClient'
import { validUrlRegexp } from 'regexps'
import { getSourceTypeFrom } from 'components/ReviewsWizard/FileSelection/urlValidationUtils'

const handleListMove = (
  moveDirection: 'up' | 'down',
  listItems: (NewInlineOption | string)[],
  chosenOption: NewInlineOption | string,
  setChosenOption: SetState<NewInlineOption | string>,
) => {
  const lengthOfList = listItems.length
  if (lengthOfList === 1) return

  const currentIndex = listItems.indexOf(chosenOption)
  const lastIndex = lengthOfList - 1

  if (moveDirection === 'down') {
    setChosenOption(listItems[(currentIndex + 1) % listItems.length])
  }
  if (moveDirection === 'up') {
    setChosenOption(listItems[currentIndex === 0 ? lastIndex : currentIndex - 1])
  }
}

const handleEnterClick = ({
  chosenOption,
  handleDocClick,
  handleFolderClick,
  handleMoreClick,
  handleSectionClick,
  handleSuggestedDocClick,
  handleLinkClick,
}: HandleEnterProps) => {
  switch (chosenOption) {
    case NewInlineOption.Doc:
      handleDocClick()
      break
    case NewInlineOption.Handbook:
      handleFolderClick(NewInlineOption.Handbook)
      break
    case NewInlineOption.Database:
      handleFolderClick(NewInlineOption.Database)
      break
    case NewInlineOption.More: {
      handleMoreClick()
      break
    }
    case NewInlineOption.Section: {
      handleSectionClick?.()
      break
    }
    case NewInlineOption.BoardView:
      handleFolderClick(NewInlineOption.BoardView)
      break
    case NewInlineOption.UrlLink: {
      handleLinkClick?.()
      break
    }
    default: {
      handleSuggestedDocClick()
    }
  }
}

export const handleKeydown = ({
  event,
  listItems,
  chosenOption,
  setChosenOption,
  handleDocClick,
  handleFolderClick,
  handleMoreClick,
  handleSectionClick,
  handleSuggestedDocClick,
  handleLinkClick,
  reset,
}: HandleKeydownProps) => {
  event.stopPropagation()

  switch (event.key) {
    case 'ArrowDown':
    case 'Tab':
      event.preventDefault()
      handleListMove('down', listItems, chosenOption, setChosenOption)
      return
    case 'ArrowUp':
      handleListMove('up', listItems, chosenOption, setChosenOption)
      return
    case 'Enter':
      handleEnterClick({
        chosenOption,
        handleDocClick,
        handleFolderClick,
        handleMoreClick,
        handleSectionClick,
        handleLinkClick,
        handleSuggestedDocClick,
      })
      return
    case 'Escape':
      reset()
  }
}

// when pasting text on the input, check if it's a review anything or almanac doc, otherwise show link form
export const handleLinkPaste = ({
  text,
  overrideFolder,
  onOpenImportModal,
  onDocMatch,
  onNoMatch,
  showSuggestions,
}: {
  text: string
  overrideFolder: AlmanacFile | null
  onOpenImportModal: ({ defaultSource, defaultEmbedLink, overrideFolder }: OnOpenImportModalProps) => void
  onDocMatch: (modifiedUrl: string) => void
  onNoMatch?: () => void
  showSuggestions?: boolean
}) => {
  if (validUrlRegexp.test(text)) {
    const reviewAnythingType = getSourceTypeFrom(text)

    if (!!reviewAnythingType) {
      onOpenImportModal({
        defaultSource: reviewAnythingType,
        defaultEmbedLink: text,
        overrideFolder: overrideFolder,
      })
      return
    }

    if (showSuggestions && isAlmanacDocUrl(text)) {
      const modifiedUrl = text.split('?')[0].split('/').at(-1) || text
      onDocMatch(modifiedUrl)

      return
    }

    onNoMatch?.()
  }
}

export const isFileInFolder = async (fileId: string, folderId: string) => {
  const res = await FolderClient.checkIfDocAlreadyExists(folderId, fileId)

  if (res.type === SUCCESS_RESPONSE_TYPE) {
    return res.data.in_folder
  }

  return true
}
